import get from 'lodash/get';
import set from 'lodash/set';
import {
  between,
  required,
  minValue,
  maxValue,
  maxLength,
  minLength,
} from 'vuelidate/lib/validators';
import translate, { formatNumber } from '@/services/translate';
import { pushAlert } from '@/services/messages/alertService';
import { stateValidator } from '@/plugins/bsStateValidator';

/**
 * Constrói definições do Vuelidate com base em definições de campos.
 * @param {import('@/entities/fieldDefinitions.js').FieldDefinitions} fieldsDefinitions Definições
 * de campos.
 * @returns {Object} Validações do vuelidate.
 */
export function validationBuilder(fieldsDefinitions) {
  const validations = {};
  Object.keys(fieldsDefinitions).forEach((property) => {
    const fieldDefinition = fieldsDefinitions[property];

    if (property === 'type' && fieldDefinition === Object) {
      return;
    }

    if (fieldDefinition.type === Object) {
      validations[property] = validationBuilder(fieldDefinition);
      return;
    }

    validations[property] = {};
    const fieldValidations = validations[property];

    if (fieldDefinition.required) {
      fieldValidations.required = required;
    }

    if (fieldDefinition.min != null && fieldDefinition.max != null) {
      fieldValidations.between = between(
        fieldDefinition.min,
        fieldDefinition.max,
      );
    } else if (fieldDefinition.min != null) {
      fieldValidations.min = minValue(fieldDefinition.min);
    } else if (fieldDefinition.max != null) {
      fieldValidations.max = maxValue(fieldDefinition.min);
    }

    if (fieldDefinition.maxLength != null) {
      fieldValidations.maxLength = maxLength(fieldDefinition.maxLength);
    }

    if (fieldDefinition.minLength != null) {
      fieldValidations.minLength = minLength(fieldDefinition.minLength);
    }
  });

  return validations;
}

/**
 * Constrói evento de blur para emissão de erros com base em validações do vuelidate.
 * Não constrói as validações, constrói apenas o evento.
 * @param {import('@/entities/fieldDefinitions.js').FieldDefinitions} fieldsDefinitions Definições
 * de campos.
 * @returns {Function} Funções para evento de blur no formato function(fieldName: string).
 */
export function blurEventBuilder(fieldsDefinitions, dataSelector = value => value) {
  return function onBlurValidableField(fieldName) {
    const validationModel = get(dataSelector(this.$v), fieldName);
    validationModel.$touch();

    const fieldDefinition = get(fieldsDefinitions, fieldName);
    const options = {
      minimumFractionDigits: fieldDefinition.precision || 0,
      maximumFractionDigits: fieldDefinition.precision || 0,
    };

    if (validationModel.between === false) {
      pushAlert({
        code: 'MA029',
        messageParams: {
          NomeDoCampo: translate(fieldDefinition.label),
          FaixaInicial: formatNumber(fieldDefinition.min, options),
          FaixaFinal: formatNumber(fieldDefinition.max, options),
        },
      });
    }

    if (validationModel.minLength === false) {
      pushAlert({
        code: 'MA010',
        messageParams: {
          NomeDoCampo: translate(fieldDefinition.label),
          NroCaracteres: fieldDefinition.minLength,
        },
      });
    }
  };
}

function checkState(vm, fieldsDefinitions, dataSelector) {
  return Object.keys(fieldsDefinitions).reduce(
    (states, fieldName) => {
      const fieldDefinition = fieldsDefinitions[fieldName];

      if (fieldName === 'type' && fieldDefinition === Object) {
        return states;
      }

      const validationModel = dataSelector(vm.$v)[fieldName];

      let state = null;

      if (fieldDefinition.type === Object) {
        state = checkState(vm, fieldDefinition, () => validationModel);
      } else {
        state = stateValidator(validationModel.$invalid);
      }

      // eslint-disable-next-line no-param-reassign
      states[fieldName] = state;

      return states;
    }, {},
  );
}

/**
 * Constrói uma função de computed que retorna os estados de validade dos campos.
 * A computed segue a mesma estrutura dos dados/definição de campos, retornando Boolean para os
 * estados de validate dos campos.
 * @param {import('@/entities/fieldDefinitions.js').FieldDefinitions} fieldsDefinitions Definições
 * de campos.
 * @param {Function} [dataSelector] Seletor de dados dentro da viewModel, para caso os dados não
 * estejam na raiz do viewModel. Opcional.
 * @returns {Function} Função para computed.
 */
export function stateComputedBuilder(fieldsDefinitions, dataSelector = model => model) {
  return function stateComputed() {
    return checkState(this, fieldsDefinitions, dataSelector);
  };
}

/**
 * Constrói um mixin com validações e utilitários de validação.
 * Inlcui:
 * - validações do vuelidate;
 * - evento de blur;
 * - computed de states.
 *
 * Exemplo de mixin gerado:
 * @example
 * {
 *   validations: {
 *     ... // vuelidate
 *   },
 *   methods: {
 *     onBlurValidableField(fieldName) {
 *       ... // Evento de blur com popup de validações
 *     },
 *   },
 *   computed: {
 *     fieldStates() {
 *       ... // estados na mesma estrutura dos dados retornando boolean.
 *     }
 *   }
 * }
 *
 * @param {Object} param Parametros de construção.
 * @param {import('@/entities/fieldDefinitions.js').FieldDefinitions} param.fieldsDefinitions
 * Definições de campos.
 * @param {String} [param.dataPath] Caminho para os dados, em string, caso não estejam na raiz do
 * viewModel.
 * @param {String} [param.blurEventName=onBlurValidableField] Nome do evento de blur.
 * @param {String} [param.stateComputedName=fieldStates] Nome da computed de estados.
 * @returns {Object} Mixin constrúido.
 */
export function mixinBuilder({
  fieldsDefinitions,
  dataPath,
  blurEventName = 'onBlurValidableField',
  stateComputedName = 'fieldStates',
}) {
  const hasDataPath = dataPath != null;
  const dataSelector = hasDataPath
    ? model => get(model, dataPath)
    : model => model;

  const computedGetter = stateComputedBuilder(
    fieldsDefinitions,
    dataSelector,
  );

  const onBlurEvent = blurEventBuilder(
    fieldsDefinitions,
    dataSelector,
  );

  const fieldValidations = validationBuilder(fieldsDefinitions);

  const validations = hasDataPath
    ? set(
      {},
      dataPath,
      fieldValidations,
    ) : fieldValidations;

  return {
    computed: {
      [stateComputedName]: computedGetter,
    },
    methods: {
      [blurEventName]: onBlurEvent,
    },
    validations,
  };
}
